import { Article } from '@wix/answers-api';
import { deserialize } from '@wix/answers-article-html-serdes';
import { ArticleComponent } from '@wix/answers-article-viewer';
import { Anchors, ConsumerSource, Step } from '@wix/cc-user-facing-common-components';
import { Box, Heading } from '@wix/design-system';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import {
  classNames as innerClassNames,
  getArticleUriFromLink,
  getCategoryUriFromLink,
  getSearchQueryFromLink,
} from '../../../../util';
import {
  logArticleAnchorClick,
  logArticleExternalLinkAction,
  logArticleImageClick,
  logArticleSolutionLinkAction,
  logArticleTabAction,
  logSearchSuggestedQuestionInArticleAction,
} from '../../../bi';
import { logArticleView } from '../../../common/api/answersAnalytics';
import { ArticleView, ArticleViewer } from '../../../common/components/ArticleViewer';
import { Loader } from '../../../common/components/Loader';
import { ScrollBox } from '../../../common/components/ScrollBox';
import { useBiEngagementEventHandlers } from '../../../common/hooks/useBiEngagementEventHandlers';
import { useHttpClient } from '../../../common/providers/httpClient';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import { setLastClickedLink } from '../../../common/reducers/lastClickedLink';
import { selectArticleState } from '../../../common/selectors/helpCenter';
import { selectWidgetSize } from '../../../common/selectors/widget';
import { experiments } from '../../../experiments';
import { appRoutes } from '../../../routes';
import { renderModel } from '../../../util/renderModel';
import { normalizeUrl } from '../../../util/urlNormalization';
import { ArticleFooter } from '../ArticleFooter';
import { ArticleHeader } from '../ArticleHeader';
import s from './styles.scss';
import { useCollapsibleComponentsBiTracker } from './useCollapsibleComponentsBiTracker';
import { useYouTubeVideoTracker } from './useYouTubeVideoTracker';

const isStudioEditor = renderModel.isStudioEditor();
const isBlocks = renderModel.isBlocks();

function addTargetBlank(target: EventTarget): void {
  if (target instanceof HTMLAnchorElement) {
    // always open all links in new tab
    target.setAttribute('target', '_blank');
  }
}

export const dataHook = 'article__viewer';

const isLandingPage = renderModel.isLandingPage();
const isFullWidth = renderModel.isFullWidth();

type ArticleContainerProps = {
  article: Article;
  onTitleHiddenChange: (value: boolean) => void;
  openCategoriesExternally?: boolean;
  sidebar?: boolean;
};

export const ArticleContainer: FC<ArticleContainerProps> = memo(function ArticleContainerComponent({
  article,
  onTitleHiddenChange,
  openCategoriesExternally,
  sidebar,
}) {
  const httpClient = useHttpClient();
  const store = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isLoading, articleHeadingVisible } = useSelector(selectArticleState);
  const widgetSize = useSelector(selectWidgetSize);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { onScroll: pageEngagementOnScroll, setInitialScrollState } = useBiEngagementEventHandlers();

  const navigateToArticle = useCallback(
    (isOtherArticle: boolean, articleUri: string, hash?: string) => {
      const pathname = isOtherArticle ? appRoutes.articleByUri({ articleUri }) : undefined; // if it is the same article we do not need new pathname(we do not need to reload article)

      navigate(
        {
          pathname,
          hash,
        },
        {
          replace: !isOtherArticle, // if the same article - it's scroll to article's anchor. Do not push to the history in this case.
        },
      );
    },
    [navigate],
  );

  const navigateToCategory = useCallback(
    (uri: string) => {
      const pathname = appRoutes.categoryByUri({
        categoryUri: encodeURIComponent(uri.substring(1)),
      });
      navigate(pathname);
    },
    [navigate],
  );

  const scrollToArticleAnchor = useCallback(
    (hash: string) => {
      const containerElement = containerRef.current;
      const anchorElement = containerElement?.querySelector<HTMLElement>(hash);
      if (!containerElement || !anchorElement) {
        return;
      }

      const { top: anchorElementTop } = anchorElement.getBoundingClientRect();
      const { top: containerElementTop } = containerElement.getBoundingClientRect();

      containerElement.scrollTo({
        top: anchorElementTop - containerElementTop + containerElement.scrollTop,
        behavior: 'smooth',
      });
    },
    [containerRef],
  );

  const onLinkClick = useCallback(
    (e: MouseEvent, href: string) => {
      const state = store.getState();
      const target = e.target as HTMLElement;
      const text = target?.textContent ?? '';
      const articleUri = getArticleUriFromLink(href);
      const searchQuery = getSearchQueryFromLink(href);

      const CTA_CLASSES = ['action-box-action-btn', 'article-log-in-button', 'article-get-started-button'];
      const isCta = CTA_CLASSES.some((cta) => target.getAttribute('class')?.includes(cta));

      dispatch(
        setLastClickedLink({
          url: href,
          text,
          pageType: 'article',
        }),
      );

      if (searchQuery) {
        e.preventDefault();
        const searchParams = new URLSearchParams();
        searchParams.set('query', searchQuery);

        dispatch(helpCenterSlice.actions.setSearchSessionId(uuid()));

        navigate({
          pathname: appRoutes.search(),
          search: searchParams.toString(),
        });

        const updatedState = store.getState();
        logSearchSuggestedQuestionInArticleAction(updatedState, {
          id: article.id,
          searchedText: searchQuery,
        });
        return;
      }

      if (articleUri) {
        e.preventDefault();
        const [linkWithoutHash, hash] = href.split('#');
        const isOtherArticle = linkWithoutHash !== encodeURI(article.url);

        const sectionEl = containerRef.current?.querySelector(`#${hash}`);
        const linkSectionText = sectionEl?.textContent ?? undefined;

        logArticleSolutionLinkAction(state, {
          article,
          linkSectionText,
          linkText: text,
          isExternalLink: isOtherArticle,
          linkUrl: href,
          isCta,
        });

        navigateToArticle(isOtherArticle, articleUri, hash);
        return;
      }

      const categoryUri = getCategoryUriFromLink(href);
      if (categoryUri && !openCategoriesExternally) {
        e.preventDefault();
        return navigateToCategory(categoryUri);
      }

      const normalizedUrl = normalizeUrl(href);

      logArticleExternalLinkAction(state, {
        id: article.id,
        title: text,
        url: normalizedUrl,
        isCta,
      });
      if (e.currentTarget) {
        addTargetBlank(e.currentTarget);
      }
    },
    [store, dispatch, openCategoriesExternally, article, navigateToArticle, navigateToCategory],
  );

  const onImgClick = useCallback(
    (src: string) => {
      const state = store.getState();
      logArticleImageClick(state, { articleId: article.id, imageSrc: src });
      window.open(src);
    },
    [store, article],
  );

  const onTabClick = useCallback(
    (selectedTab: number, currentTabSlugs: string[]) => {
      const selectedTabURLSlug = currentTabSlugs[selectedTab];
      const state = store.getState();

      logArticleTabAction(state, {
        tabName: selectedTabURLSlug,
        tabIndex: selectedTab,
        articleId: article.id,
      });
    },
    [store, article],
  );

  const onAnchorClick = useCallback(
    async (text: string) => {
      const state = store.getState();
      logArticleAnchorClick(state, { id: article.id, clickedValue: text });
    },
    [store, article],
  );

  const onScroll = useCallback(
    (e) => {
      if (!titleRef.current || !containerRef.current) {
        return;
      }
      const { scrollTop } = containerRef.current;
      const { height } = titleRef.current.getBoundingClientRect();
      const headingEnabled = scrollTop > height + 18;
      if (headingEnabled !== articleHeadingVisible) {
        dispatch(helpCenterSlice.actions.setArticleHeadingVisible(headingEnabled));
      }

      pageEngagementOnScroll(e);
    },
    [articleHeadingVisible, pageEngagementOnScroll, dispatch],
  );

  useEffect(() => {
    onTitleHiddenChange(articleHeadingVisible);
  }, [articleHeadingVisible, onTitleHiddenChange]);

  const components: ArticleComponent[] = deserialize(article.content, article.locale);
  const anchors: Step[] = components
    .filter((component) => component.type === 'heading' && component.value && component.value.anchorId)
    .map((component: any) => ({
      id: component.value.anchorId,
      text: component.value.text,
      level: component.value.level,
    }));

  useEffect(() => {
    const handleImageLoading = async () => {
      const imagesElements = containerRef.current?.querySelectorAll('img') ?? [];

      if (imagesElements.length > 0) {
        await Promise.all(
          Array.from(imagesElements).map(
            (image) =>
              new Promise((resolve) => {
                image.onload = resolve;
                image.onerror = resolve;
              }),
          ),
        );
      }

      setImagesLoaded(true);
    };

    void handleImageLoading();
  }, []);

  useCollapsibleComponentsBiTracker(containerRef, article.id);
  useYouTubeVideoTracker(containerRef, article.id);

  useLayoutEffect(() => {
    if (!imagesLoaded || !location.hash || isLoading) {
      return;
    }
    scrollToArticleAnchor(location.hash);
  }, [location.hash, imagesLoaded, scrollToArticleAnchor, isLoading]);

  // do not need to add heading gap for Full Page and Landing Page - there are no Search and Breadcrumbs
  const addHeadingGap = articleHeadingVisible && !sidebar && !isLandingPage;
  const className = innerClassNames([
    s.articleContainer,
    addHeadingGap && s.articleContainerHeadingGap,
    sidebar && s.articleContainerSidebar,
    isBlocks && s.articleContainerBlocks,
  ]);

  useEffect(() => {
    logArticleView(article?.id, httpClient);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      setInitialScrollState(containerRef.current);
    }
  }, [widgetSize, article.id, setInitialScrollState]);

  return (
    <ScrollBox
      className={className}
      ref={containerRef}
      onScroll={onScroll}
      overflow="scroll"
      dataHook={dataHook}
    >
      <Box
        className={classNames(s.articleTitle, { [s.articleTitleStudioEditor]: true })}
        marginTop={isFullWidth ? 'SP4' : 0}
      >
        <div ref={titleRef}>
          <Heading size="extraLarge" dataHook="article-container__title">
            {article.title}
          </Heading>
        </div>
      </Box>
      {isLoading ? (
        <Box display="block" marginTop="45%">
          <Loader dataHook="article__loader" height="100%" verticalAlign="middle" />
        </Box>
      ) : (
        <Box className={s.articleViewerWrapper} direction="vertical">
          <ArticleHeader article={article} />
          <Box direction="vertical" gap="SP3">
            <Box direction="vertical">
              {anchors.length > 0 ? (
                <Box paddingTop="SP4">
                  <Anchors
                    onClick={onAnchorClick}
                    consumerSource={ConsumerSource.WIDGET}
                    steps={anchors}
                    showScrollToTop={articleHeadingVisible}
                    containerRef={containerRef}
                    locale={article.locale}
                  />
                </Box>
              ) : null}
              <ArticleViewer
                key={article.id}
                components={components}
                locale={article.locale}
                onLinkClick={onLinkClick}
                onImgClick={onImgClick}
                // selectedTabsOnLoad is required to provide by Answers API, but it has default in case of nullable
                // @ts-expect-error:
                tabsProps={{ onTabClick, selectedTabsOnLoad: undefined }}
                chatbotArticleView={ArticleView.FULL}
              />
            </Box>
            <ArticleFooter article={article} />
          </Box>
        </Box>
      )}
    </ScrollBox>
  );
});
